import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Button, LogoLoader, OtpInput } from '@gokrew/ui';
import { TranslationNS } from '@gokrew/models/constants';
import {
  sendSignatureGateCode,
  verifySignatureGateCode,
} from '@/api/signature-requests';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';
import {
  useContinueSigning,
  useSignatureGate,
} from '@/hooks/useSignatureRequests';
import styles from './Sign.module.css';

interface ErrorBody {
  message?: string | string[];
  errorCode?: string;
  errorDetails?: { attemptsLeft?: number };
}

const bodyOf = (error: unknown): ErrorBody | null =>
  isAxiosError(error) ? ((error.response?.data as ErrorBody) ?? null) : null;

/**
 * The KollabHR signing gate (D74, spec 6.1): every emailed signing link lands
 * here, never on the signing service. An employee must be logged in as the
 * person it was sent to; anyone without an account confirms an emailed
 * 6-digit code. Only then does the signing page open, as its own page (D75).
 */
export function SignGatePage() {
  const { t, i18n } = useTranslation(TranslationNS.SignatureRequests);
  const { token: gateToken } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { token: session } = useEmployeePortalAuth();
  const gate = useSignatureGate(gateToken);
  const continueSigning = useContinueSigning();
  const [code, setCode] = useState('');
  const started = useRef(false);

  const sendCode = useMutation({
    mutationFn: () => sendSignatureGateCode(gateToken!),
  });
  const verify = useMutation({
    mutationFn: () => verifySignatureGateCode(gateToken!, code),
    onSuccess: ({ url }) => window.location.assign(url),
  });

  const view = gate.data;

  // Someone without a session (a candidate) reads the gate in the
  // document's language, like the email that brought them here.
  useEffect(() => {
    if (!session && view?.language && i18n.language !== view.language) {
      void i18n.changeLanguage(view.language);
    }
  }, [i18n, session, view?.language]);

  // Logged in as an employee: go straight on to the signing page, once.
  useEffect(() => {
    if (
      view?.step === 'login' &&
      view.portal === 'employee' &&
      view.requestId &&
      session &&
      !started.current
    ) {
      started.current = true;
      continueSigning.mutate(view.requestId);
    }
  }, [continueSigning, session, view]);

  if (gate.isLoading) return <LogoLoader />;

  if (gate.isError || !view) {
    return (
      <div className={styles.gate}>
        <h2 className={styles.heading}>{t('gate.invalid_title')}</h2>
        <p className={styles.subtext}>{t('gate.invalid')}</p>
      </div>
    );
  }

  const header = (
    <>
      <p className={styles.eyebrow}>{view.companyName}</p>
      <h2 className={styles.heading}>{view.title}</h2>
    </>
  );

  if (view.step !== 'login' && view.step !== 'code') {
    return (
      <div className={styles.gate}>
        {header}
        <p className={styles.subtext}>{t(`gate.${view.step}`)}</p>
      </div>
    );
  }

  if (view.step === 'login') {
    if (view.portal === 'company') {
      return (
        <div className={styles.gate}>
          {header}
          <p className={styles.subtext}>{t('gate.company')}</p>
        </div>
      );
    }
    if (!session) {
      return (
        <div className={styles.gate}>
          {header}
          <p className={styles.subtext}>{t('gate.login')}</p>
          <Button
            className={styles.button}
            onClick={() =>
              navigate(
                `/login?redirect=${encodeURIComponent(`/sign/${gateToken}`)}`
              )
            }
          >
            {t('gate.login_button')}
          </Button>
        </div>
      );
    }
    const refused = bodyOf(continueSigning.error);
    return (
      <div className={styles.gate}>
        {header}
        {continueSigning.isError &&
        // Fields to fill in first: the page is already moving there.
        refused?.errorCode !== 'ANSWERS_REQUIRED' ? (
          <>
            <p className={styles.error} role="alert">
              {refused?.errorCode === 'NOT_YOUR_TURN'
                ? t('gate.waiting')
                : t('gate.not_yours')}
            </p>
            <Link to="/signatures" className={styles.link}>
              {t('gate.my_documents')}
            </Link>
          </>
        ) : (
          <>
            <p className={styles.subtext}>{t('gate.opening')}</p>
            <Button
              className={styles.button}
              disabled={continueSigning.isPending}
              onClick={() => continueSigning.mutate(view.requestId!)}
            >
              {t('gate.open')}
            </Button>
          </>
        )}
      </div>
    );
  }

  // Code step: people without an account.
  const verifyError = bodyOf(verify.error);
  const codeError = bodyOf(sendCode.error);
  const sent = sendCode.isSuccess || view.codeSent;
  return (
    <div className={styles.gate}>
      {header}
      <p className={styles.subtext}>
        {sent
          ? t('gate.code_sent', { email: view.maskedEmail })
          : t('gate.code_intro', { email: view.maskedEmail })}
      </p>
      {sent && (
        <OtpInput
          value={code}
          onChange={setCode}
          error={verify.isError}
          disabled={verify.isPending}
          onEnter={() => code.length === 6 && verify.mutate()}
          groupLabel={t('gate.code_label')}
        />
      )}
      {verify.isError && (
        <p className={styles.error} role="alert">
          {verifyError?.errorCode === 'CODE_LOCKED'
            ? t('gate.code_locked')
            : verifyError?.errorCode === 'CODE_EXPIRED'
              ? t('gate.code_expired')
              : verifyError?.errorCode === 'WRONG_CODE'
                ? t('gate.code_wrong', {
                    count: verifyError.errorDetails?.attemptsLeft ?? 0,
                  })
                : t('gate.error')}
        </p>
      )}
      {sendCode.isError && (
        <p className={styles.error} role="alert">
          {codeError?.errorCode === 'CODE_TOO_SOON'
            ? t('gate.code_too_soon')
            : t('gate.error')}
        </p>
      )}
      {sent && (
        <Button
          className={styles.button}
          disabled={code.length !== 6 || verify.isPending}
          onClick={() => verify.mutate()}
        >
          {t('gate.verify')}
        </Button>
      )}
      <Button
        variant={sent ? 'ghost' : 'primary'}
        className={styles.button}
        disabled={sendCode.isPending}
        onClick={() => {
          setCode('');
          verify.reset();
          sendCode.mutate();
        }}
      >
        {sent ? t('gate.resend') : t('gate.send_code')}
      </Button>
    </div>
  );
}

export default SignGatePage;
